'use client'

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'

import {
  fetchLedgerPage,
  type StatsPreset,
  statsRange,
} from '@/features/settings/billing/credits/lib/credits'
import type { ApiBillingView } from '@/features/settings/billing/credits/lib/mappers'
import {
  createCreditCheckout,
  fetchApiBilling,
  fetchCreditSpendStats,
  listCreditLedger,
  updateApiBillingSettings,
} from '@/service/api-billing.api'
import { authAtom } from '@/state/auth'
import type { UpdateApiBillingSettingsRequest } from '@/types/generated/public_api'

export const apiBillingKeys = {
  all: (userId?: string | null) => ['api-billing', userId] as const,
  billing: (userId?: string | null) => [...apiBillingKeys.all(userId), 'billing'] as const,
  ledger: (userId?: string | null, keyId?: string) =>
    [...apiBillingKeys.all(userId), 'ledger', keyId ?? null] as const,
  stats: (userId?: string | null, preset?: StatsPreset) =>
    [...apiBillingKeys.all(userId), 'stats', preset] as const,
}

function useAuthState() {
  const { isAuthenticated, user } = useAtomValue(authAtom)
  return { enabled: isAuthenticated, userId: user?.userId }
}

/**
 * Balance, weekly usage, settings, saved card and credit price. Refetches on focus: a declined refill
 * turns refill off and a dispute removes the card server-side, and the page must show that
 * rather than the last state it held.
 */
export function useApiBilling() {
  const { enabled, userId } = useAuthState()
  return useQuery({
    queryKey: apiBillingKeys.billing(userId),
    queryFn: fetchApiBilling,
    enabled,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  })
}

/**
 * Never retried: every refused attempt counts toward the step-up throttle. On success the
 * stored settings from the response replace the cached copy — never the form's local one.
 */
export function useUpdateApiBillingSettings() {
  const queryClient = useQueryClient()
  const { userId } = useAuthState()
  return useMutation({
    mutationFn: (payload: UpdateApiBillingSettingsRequest) => updateApiBillingSettings(payload),
    onSuccess: (settings) => {
      queryClient.setQueryData<ApiBillingView>(apiBillingKeys.billing(userId), (prev) =>
        prev ? { ...prev, settings } : prev
      )
    },
  })
}

export function useCreateCreditCheckout() {
  return useMutation({ mutationFn: createCreditCheckout })
}

/** Credit history, newest first, a page at a time. See `fetchLedgerPage` for the key filter. */
export function useCreditLedger(keyId?: string) {
  const { enabled, userId } = useAuthState()
  return useInfiniteQuery({
    queryKey: apiBillingKeys.ledger(userId, keyId),
    queryFn: ({ pageParam }) =>
      fetchLedgerPage(listCreditLedger, { keyId, cursor: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
    enabled,
    retry: 1,
  })
}

export function useCreditSpendStats(preset: StatsPreset) {
  const { enabled, userId } = useAuthState()
  return useQuery({
    queryKey: apiBillingKeys.stats(userId, preset),
    queryFn: () => fetchCreditSpendStats(statsRange(preset, new Date())),
    enabled,
    staleTime: 60 * 1000,
    retry: 1,
  })
}

/**
 * Refetches everything credits-related. After a purchase lands, the ledger and stats must move
 * with the balance: the running balance column is worked back from it.
 */
export function useInvalidateApiBilling() {
  const queryClient = useQueryClient()
  const { userId } = useAuthState()
  return () => queryClient.invalidateQueries({ queryKey: apiBillingKeys.all(userId) })
}

/**
 * Drops every loaded ledger page and starts over from the first. The retry after a failed page
 * must not re-send the cursor it failed on — "Invalid cursor." would come straight back.
 */
export function useResetCreditLedger() {
  const queryClient = useQueryClient()
  const { userId } = useAuthState()
  return (keyId?: string) =>
    queryClient.resetQueries({ queryKey: apiBillingKeys.ledger(userId, keyId) })
}
